import { gql, useQuery } from "@apollo/client";
import { getFetchPolicyOptions } from "api/utils";
import { useGetContractIdData } from "providers/ContractIdProvider";
import { useKeycloak } from "providers/KeycloakProvider";

const NEW_DOCUMENTS_CHECK = gql`
  query CheckNewDocuments($contactId: Long, $filterTags: [String]) {
    getLastLogins
    contact(id: $contactId) {
      documents(filterTags: $filterTags) {
        identifier
        lastModified
        created
        fileName
      }
      portfolios {
        id
        documents(filterTags: $filterTags) {
          identifier
          lastModified
          created
          fileName
        }
      }
    }
  }
`;

const filterTags: string[] = ["Online"];

interface DocumentsCheckQuery {
  getLastLogins: number[];
  contact: {
    documents: Document[];
    portfolios: {
      id: number;
      documents: Document[];
    }[];
  };
}

export const useCheckForNewDocuments = () => {
  const { selectedContactId } = useGetContractIdData();
  const { loading, error, data } = useQuery<DocumentsCheckQuery>(NEW_DOCUMENTS_CHECK, {
    variables: {
      contactId: selectedContactId,
      filterTags,
    },
    skip: !selectedContactId,
    fetchPolicy: "cache-and-network",
  });

  const previousLoginTime = data ? getPreviousLoginTime(data.getLastLogins) : undefined;

  return {
    loading,
    error,
    data:
      data && selectedContactId
        ? latestNewDocumentTime(
            [...data.contact.documents, ...data.contact.portfolios.flatMap((p) => p.documents)],
            data.getLastLogins
          )
        : false,
    newSinceTimestamp: previousLoginTime,
  };
};

/** Returns the previous login timestamp (ms), or undefined if not available. */
const getPreviousLoginTime = (lastLogins: number[]): number | undefined => {
  const sorted = [...lastLogins].sort((a, b) => b - a);
  return sorted[1];
};

/** Returns the latest lastModified timestamp (ms) of documents newer than the previous login, or false if none. */
const latestNewDocumentTime = (documents: Document[], lastLogins: number[]): number | false => {
  const sorted = [...lastLogins].sort((a, b) => b - a);
  const previousLogin = sorted[1];
  if (previousLogin === undefined) return false;
  const docTime = (doc: Document) =>
    Math.max(new Date(doc.lastModified).getTime(), new Date(doc.created).getTime());
  const newDocs = documents.filter((doc) => docTime(doc) > previousLogin);
  if (newDocs.length === 0) return false;
  return Math.max(...newDocs.map(docTime));
};

export interface Document {
  identifier: string;
  fileName: string;
  created: string;
  lastModified: string;
}
